import type { Tone } from '@krp/sale-ui'

/**
 * Намерение кассира в окне хода проведения: какая форма открыта, что набрано, что отказал агент, какие заметки висят после продажи.
 * Чистая часть без сети: отправку делает хук, а итог (отказ или результат) возвращает действием. Сами «слова человека» агенту —
 * отложить, повторить проведение, чек коррекции, закрыть с причиной, оплата отменена вручную — идут через контроллер сессии.
 */

/** Действия под «Подробнее» со своей формой или одним нажатием (кроме «Отложить продажу» — оно в общем слоте `park`). */
export type OwnToolKey = 'correction' | 'repunch-number' | 'close' | 'confirm-void'

/** Что сейчас уходит агенту: кнопка занята, повторное нажатие гасится. */
export type OwnSending = 'park' | 'repeat' | OwnToolKey | 'print'

export interface OwnNotice {
  /** `print` — допечатать; `double` — возможно два чека; `parked` — слова агента об откладывании; `info` — прочее. */
  key: string
  text: string
  tone: Tone
  /** Есть кнопка действия (сейчас только «Допечатать»). */
  action: 'print' | null
}

export interface OwnWordsState {
  parkOpen: boolean
  tool: { key: OwnToolKey; values: Readonly<Record<string, string>> } | null
  sending: OwnSending | null
  /** Последний отказ агента на слово человека, для операции `operationId`: показывается под основной кнопкой дословно. */
  refusal: { operationId: string; code: string | null; text: string } | null
  notices: readonly OwnNotice[]
}

export const initialOwnWords: OwnWordsState = { parkOpen: false, tool: null, sending: null, refusal: null, notices: [] }

export type OwnWordsAction =
  | { type: 'park-opened' }
  | { type: 'park-closed' }
  | { type: 'tool-opened'; key: OwnToolKey }
  | { type: 'tool-field'; field: string; value: string }
  | { type: 'tool-closed' }
  | { type: 'sending'; what: OwnSending | null }
  | { type: 'refused'; operationId: string; code: string | null; text: string }
  /** Слово принято агентом: формы закрываются, отказ снимается. */
  | { type: 'accepted' }
  | { type: 'notice-added'; notice: OwnNotice }
  | { type: 'notice-removed'; key: string }
  /** Началась другая продажа или операция: формы и отказ прежней не переносятся. */
  | { type: 'operation-changed' }

/** Значения формы при открытии: пустые поля, виды по умолчанию. */
function defaultValues(key: OwnToolKey): Readonly<Record<string, string>> {
  switch (key) {
    case 'correction':
      return { lastFiscalDocument: '', correctionType: 'self', baseDate: '', baseNumber: '' }
    case 'repunch-number':
      return { lastFiscalDocument: '' }
    case 'close':
      return { charged: '', checked: '', reason: '', rrn: '', authCode: '' }
    case 'confirm-void':
      return {}
  }
}

export function ownWordsReducer(state: OwnWordsState, action: OwnWordsAction): OwnWordsState {
  switch (action.type) {
    case 'park-opened':
      return state.sending === null ? { ...state, parkOpen: true, tool: null } : state
    case 'park-closed':
      return state.sending === null ? { ...state, parkOpen: false } : state
    case 'tool-opened':
      return state.sending === null ? { ...state, parkOpen: false, tool: { key: action.key, values: defaultValues(action.key) } } : state
    case 'tool-field':
      return state.tool === null || state.sending !== null ? state : { ...state, tool: { ...state.tool, values: { ...state.tool.values, [action.field]: action.value } } }
    case 'tool-closed':
      return state.sending === null ? { ...state, tool: null } : state
    case 'sending':
      return { ...state, sending: action.what }
    case 'refused':
      return { ...state, sending: null, refusal: { operationId: action.operationId, code: action.code, text: action.text } }
    case 'accepted':
      return { ...state, sending: null, parkOpen: false, tool: null, refusal: null }
    case 'notice-added':
      return { ...state, notices: [...state.notices.filter((item) => item.key !== action.notice.key), action.notice] }
    case 'notice-removed':
      return { ...state, notices: state.notices.filter((item) => item.key !== action.key) }
    case 'operation-changed':
      return { ...state, parkOpen: false, tool: null, sending: null, refusal: null }
  }
}

// ---- проверка полей форм (чистые функции) ----

/** Дата основания чека коррекции в виде агента: `ГГГГ.ММ.ДД`. */
export const BASE_DATE_PATTERN = /^\d{4}\.\d{2}\.\d{2}$/

/** Номер документа с кассы: целое положительное. `null` — не годится. */
export function parseDocumentNumber(text: string): number | null {
  const value = text.trim()
  if (!/^\d{1,9}$/.test(value)) return null
  const number = Number(value)
  return number > 0 ? number : null
}

/** Ошибки полей формы: ключ поля → слова. Пустой объект — форму можно отправлять. */
export function toolErrors(key: OwnToolKey, values: Readonly<Record<string, string>>): Readonly<Record<string, string>> {
  const errors: Record<string, string> = {}
  const documentNumber = (): void => {
    if (parseDocumentNumber(values.lastFiscalDocument ?? '') === null) errors.lastFiscalDocument = 'Введите номер последнего документа, как на кассе (целое число).'
  }
  switch (key) {
    case 'correction': {
      documentNumber()
      const date = (values.baseDate ?? '').trim()
      if (date !== '' && !BASE_DATE_PATTERN.test(date)) errors.baseDate = 'Дата в виде ГГГГ.ММ.ДД, например 2026.10.19.'
      if (values.correctionType === 'instruction' && (values.baseNumber ?? '').trim() === '') errors.baseNumber = 'Для коррекции по предписанию нужен номер предписания.'
      break
    }
    case 'repunch-number':
      documentNumber()
      break
    case 'close':
      if (values.charged !== 'yes' && values.charged !== 'no') errors.charged = 'Выберите: деньги списаны или нет.'
      // Агент требует «что проверили» и причину в ОБОИХ исходах: без них он отвечает 400.
      if ((values.checked ?? '').trim() === '') errors.checked = 'Укажите, что вы проверили на терминале.'
      if ((values.reason ?? '').trim() === '') errors.reason = 'Напишите причину своими словами, например «слип с отказом банка».'
      if (errors.charged !== undefined) break
      if (values.charged === 'yes' && (values.rrn ?? '').trim() === '') errors.rrn = 'Укажите RRN платежа, как на слипе терминала.'
      break
    case 'confirm-void':
      break
  }
  return errors
}
